import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BreadcrumbsComponent } from '../../shared/components/breadcrumbs/breadcrumbs.component';
import { AlertComponent } from '../../shared/components/dialogs/alert/alert.component';
import { LoaderDialogComponent } from '../../shared/components/loader-dialog/loader-dialog.component';
import { BreadcrumbsOptions } from "../../shared/models/breadcrumbs-model";
import { OrderItem } from '../models/orderItem';
import { OrdersService } from '../services/orders/orders.service';
import { BudgetsComponent } from './budgets-component/budgets-component.component';
import { RequestedItemsComponent } from './requested-items-component/requested-items-component.component';

@Component({
  selector: 'app-order-details',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    TranslateModule,
    FlexLayoutModule,
    MatTableModule,
    BreadcrumbsComponent,
    MatTabsModule,
    RequestedItemsComponent,
    BudgetsComponent,
  ],
  templateUrl: './order-details.component.html',
  styleUrl: './order-details.component.scss',
  standalone: true
})
export class OrderDetailsComponent implements OnInit {
  orderId: string | null;
  projectName: string | null = null;
  projectDescription: string | null = null;
  projectType: string | null = null;
  selectedTabIndex: number = 0;
  orderStatus: string | null = null;
  generalRemarks: string = '';
  rejectionJustification: string = '';
  cancelationJustification: string = '';
  itemsData: OrderItem[] = [];

  isRequester: boolean = false;
  approvalPending: boolean = false;
  isRejected: boolean = false;
  isCancelled: boolean = false;
  showReleaseBudgetsToApprovalButton: boolean = false;

  selectedSupplierIds: number[] = [];

  public requestedItems: OrderItem[] = [];

  breadcrumbsData: BreadcrumbsOptions = {
    title: 'order-details.title',
    buttons: [
      {
        label: 'button.back',
        style: 'blue',
        do: () => this.navigateToDashboard()
      }
    ]
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private ordersService: OrdersService,
    private translate: TranslateService
  ) {
    this.orderId = this.route.snapshot.paramMap.get('id');
  }

  ngOnInit() {
    if (localStorage.getItem('tab') === '1') {
      this.selectedTabIndex = 1;
      localStorage.removeItem('tab');
    }

    this.ordersService.getOrderDetails(Number.parseInt(this.orderId!)).pipe(take(1)).subscribe({
        next: (data: OrderItem[]) => {
          this.itemsData = data;
          console.log(data);
          if (data.length > 0) {
            this.projectDescription = data[0].projectDescription;
            this.projectName = data[0].projectName;
            this.projectType = data[0].projectType;
            this.orderStatus = data[0].status;
            this.generalRemarks = data[0].generalRemarks;
            this.cancelationJustification = data[0].cancellationJustification;
            this.rejectionJustification = data[0].rejectionJustification;
          }
          const storedRequester = sessionStorage.getItem('isRequester');

          this.isRequester = storedRequester ? JSON.parse(storedRequester) : false;

          const statusFlags = {
            "WAITING_REQUEST_APPROVAL": { approvalPending: true },
            "NOT_SENT": { approvalPending: true },
            "REJECTED": { isRejected: true },
            "CANCELLED_ORDER": { isCancelled: true },
            "CANCELLED_BUDGET": { isCancelled: true }
          };

          const flags = statusFlags[this.orderStatus as keyof typeof statusFlags] || {};

          Object.assign(this, flags);

          this.updateBreadcrumbButton();
        },
        error: (error) => {
          if (error.status === 404) {
            this.dialog.open(AlertComponent, {
              data: {
                message: 'order-details.order-not-found',
                title: 'alert.information',
                complement: '',
                param: null,
                value: null
              }
            });
          }}
        },
      );

  }

  onTabChange(index: number) {
    this.selectedTabIndex = index;
    this.updateBreadcrumbButton();
  }

  onSuppliersSelected(suppliers: any[]) {
    this.selectedSupplierIds = suppliers;
    if (this.selectedTabIndex === 1) {
      this.updateBreadcrumbButton();
    }
  }

  shouldShowRequestedItemsDirectly(): boolean {
    return this.isRequester || this.approvalPending || this.isRejected || this.isCancelled
  }

  private updateBreadcrumbButton() {
    if (this.selectedTabIndex === 0) {
      this.breadcrumbsData.buttons![0] = {
        hide: false,
        label: 'button.back',
        router: '/orders-dashboard',
        style: 'blue',
        do: () => this.navigateToDashboard()
      };
    } else if (this.selectedTabIndex === 1) {
      if (this.selectedSupplierIds.length > 0) {
        this.breadcrumbsData.buttons![0] = {
          hide: false,
          label: 'button.send-email',
          router: './',
          style: 'blue',
          do: () => this.onSendSupplierEmail()
        };
      } else if (!!this.showReleaseBudgetsToApprovalButton) {
        this.breadcrumbsData.buttons![0] = {
          hide: false,
          label: 'button.release-budgets-for-approval',
          router: null,
          style: 'wider blue',
          do: () => this.releaseBudgetsToApproval()
        };
      } else {
        this.breadcrumbsData.buttons![0].hide = true;
      }
    }
  }

  onSendSupplierEmail() {
    this.ordersService.getOrderDetails(+this.orderId!).subscribe({
      next: (data: OrderItem[]) => {
        this.requestedItems = data;
        const itemsList = this.requestedItems
          .map(item => `Item: ${item.partNumber} - ${item.description} - Quantidade: ${item.requestedQuantity}`)
          .join('\n');
        const projectDescription = this.requestedItems[0].projectDescription ? this.requestedItems[0].projectDescription.substring(0, 30) : '';
        const projectType = this.requestedItems[0].projectType ? this.requestedItems[0].projectType : '';
        const emailBody = this.translate.instant('email.requestBudget', { itemsList });
        const subject = encodeURIComponent(`[KUEHNE+NAGEL] - Solicitação de Orçamento - Pedido ${this.orderId} - ${projectType}: ${projectDescription}`);
        const body = encodeURIComponent(emailBody);
        const to = environment.budgetEmail;
        this.ordersService.getEmailsBySupplierIds(this.selectedSupplierIds).subscribe({
          next: (emails) => {
            const bcc = encodeURIComponent(emails.join(','));
            window.location.href = `mailto:${to}?bcc=${bcc}&subject=${subject}&body=${body}`;
          }
        });
      },
    });
  }

  handleBudgetsPendingApprovalChange(budgetsPendingApproval: boolean) {
    this.showReleaseBudgetsToApprovalButton = budgetsPendingApproval;
    if (this.selectedTabIndex === 1) {
      this.updateBreadcrumbButton();
    }
  }

  releaseBudgetsToApproval() {
    const loader = this.dialog.open(LoaderDialogComponent, {
          disableClose: true,
        });
    this.ordersService.releaseBudgetsToApproval(this.orderId)
    .pipe(finalize(() => loader.close()))
    .subscribe({
      next: () => {
        this.dialog.open(AlertComponent, {
          data: {
            title: "alert.information",
            message: 'budget.budgetsReleased'
          }
        }).afterClosed().subscribe(() => {
          this.navigateToDashboard();
        });
      },
      error: () => {
        this.dialog.open(AlertComponent, {
          disableClose: true,
          data: {
            message: 'error.releaseBudgets',
            title: 'alert.information'
          },
        });
      }
    });
  }
  navigateToDashboard(){
    sessionStorage.clear();
    this.router.navigate(['/orders-dashboard']);
  }
}
